import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { getWeekStart, getWeekRange } from '../lib/utils';

/**
 * WeekJumper — clickable week-range chip that opens a proper shadcn Calendar
 * (react-day-picker) so users can jump directly to any pay week.
 *
 * Picking a date auto-snaps to that ISO week's Monday and calls onChange —
 * no extra Confirm click.
 *
 * Props:
 *   weekStart:  current ISO Monday (YYYY-MM-DD)
 *   onChange:   fn(newWeekStart) — receives the new Monday
 *   testId:     optional data-testid override (default: "week-jump-trigger")
 */
export default function WeekJumper({ weekStart, onChange, testId = 'week-jump-trigger' }) {
  const [open, setOpen] = useState(false);
  const selectedDate = weekStart ? new Date(weekStart + 'T00:00:00') : new Date();

  const handleSelect = (date) => {
    if (!date) return;
    // Format to YYYY-MM-DD in the user's local timezone (avoid UTC offset drift).
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const iso = `${yyyy}-${mm}-${dd}`;
    const monday = getWeekStart(iso);
    if (monday && monday !== weekStart) onChange(monday);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 px-4 gap-2 rounded-lg font-medium border-slate-200 hover:border-blue-400 dark:border-slate-700 dark:hover:border-blue-500/60 bg-muted/50 hover:bg-muted"
          data-testid={testId}
          title="Click to jump to a specific week"
        >
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          <span>{getWeekRange(weekStart)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-auto p-0" data-testid="week-jump-popover">
        <div className="px-3 py-2 border-b text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          Pick any date — jumps to that week
        </div>
        <div data-testid="week-jump-calendar">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            initialFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
